import { ColumnDefinition, GridRow, TransformedRow, FieldMap } from 'models/DataGridTypes';

/**
 * Creates a field-to-column-name mapping for efficient lookups
 * Maps from Field identifiers (e.g., "field_0") to ColName values (e.g., "l1categoryguid")
 *
 * @param columns - Array of column definitions from the API response
 * @returns Object mapping Field to ColName
 */
export const createFieldMap = (columns: ColumnDefinition[]): FieldMap => {
  if (!columns || !Array.isArray(columns)) {
    throw new Error('Invalid columns array');
  }

  return columns.reduce<FieldMap>((acc, column) => {
    acc[column.Field] = column.ColName;
    return acc;
  }, {});
};

/**
 * Filters columns to exclude hidden ones
 * Returns only columns with ColType !== 'Hidden'
 *
 * @param columns - Array of column definitions from the API response
 * @returns Filtered array of visible columns
 */
export const filterVisibleColumns = (columns: ColumnDefinition[]): ColumnDefinition[] => {
  if (!columns || !Array.isArray(columns)) {
    throw new Error('Invalid columns array');
  }

  return columns.filter((column) => column.ColType !== 'Hidden');
};

/**
 * Transforms a single row of grid data using ColName keys instead of field_N keys
 * Maps from generic field names to semantic column names for easier consumption
 *
 * @param row - Single row from grid data with field_0, field_1, etc. as keys
 * @param fieldMap - Field to ColName mapping (created via createFieldMap)
 * @returns Object with ColName keys instead of field_N keys
 *
 * @example
 * const fieldMap = createFieldMap(columns);
 * const transformedRow = transformRowData(row, fieldMap);
 * // Before: { field_0: "value", field_1: "name", ... }
 * // After: { l1categoryguid: "value", l1categoryname: "name", ... }
 */
export const transformRowData = (row: GridRow, fieldMap: FieldMap): TransformedRow => {
  const transformed: TransformedRow = {};

  Object.entries(row).forEach(([field, value]) => {
    const colName = fieldMap[field];
    if (colName) {
      transformed[colName] = value;
    }
  });

  return transformed;
};

/**
 * Transforms all rows in a grid response using ColName keys
 * Convenient wrapper that handles field map creation and row transformation
 *
 * @param rows - Array of rows from grid data
 * @param columns - Array of column definitions from the API response
 * @returns Array of transformed rows with ColName keys
 *
 * @example
 * const transformedData = transformGridData(response.data.data, response.data.columns);
 */
export const transformGridData = (
  rows: GridRow[],
  columns: ColumnDefinition[]
): TransformedRow[] => {
  const fieldMap = createFieldMap(columns);
  return rows.map((row) => transformRowData(row, fieldMap));
};
